import * as React from "react";
import { ArrowLeft, Check, Building2, X, Plus, Clock, Ban } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Drawer, DrawerContent } from "../UI/drawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../UI/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "../UI/alert-dialog";
import { Button } from "../UI/button";
import useIsMobile from "../../Hooks/useIsMobile";
import { cn } from "../../Lib/Cn";
import { getUploadUrl, isUserPremium } from "../../Utils/Utilities";
import AddBusinessDialog from "./AddBusinessDialog";
import { switchAgency } from "../../Utils/AgencySwitch";
import { respondInvitation } from "../../Services/LoginAPI";
import { getAuthData } from "../../Utils/AuthUtils";
import { setGlobalLoading } from "../../Store/Actions";

// Helper function to get agency logo url, checking 'B' local storage cookie if not present in agency object
const getAgencyLogoUrl = (agency) => {
  let logoPath = agency.Logo;
  if (!logoPath) {
    try {
      const bCookieRaw = localStorage.getItem('B');
      if (bCookieRaw) {
        const bCookie = JSON.parse(bCookieRaw);
        if (Array.isArray(bCookie)) {
          const matched = bCookie.find(row => Number(row.AgencyID) === Number(agency.AgencyID));
          if (matched && matched.Logo) {
            logoPath = matched.Logo;
          }
        }
      }
    } catch (e) {
      console.error("Error parsing B cookie for logo:", e);
    }
  }
  return logoPath ? getUploadUrl(`Uploads/${logoPath}`) : null;
};

// Helper component for mobile Drawer Body exactly matching TaskCategoryManager.jsx style
const MobileDrawerBody = ({
  onClose,
  userAgencies,
  currentAgencyID,
  handleAgencySwitch,
  onAddBusiness,
  handleAcceptInvitation,
  handleRejectInvitation,
  scrollToPending = false,
}) => {
  const activeAgencies = userAgencies.filter(a => Number(a.Status) !== 0);
  const pendingAgencies = userAgencies.filter(a => Number(a.Status) === 0);
  const pendingRef = React.useRef(null);

  React.useEffect(() => {
    if (scrollToPending && pendingRef.current) {
      const timer = setTimeout(() => {
        pendingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [scrollToPending]);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-16 shrink-0 border-b border-zinc-100">
        <div className="flex gap-4 items-center">
          <button
            type="button"
            tabIndex={-1}
            onClick={onClose}
            className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-[24px] w-[24px]" />
          </button>
          <p className="text-lg font-semibold text-gray-900">Select Business</p>
        </div>
        <button
          type="button"
          onClick={onAddBusiness}
          className="w-auto h-auto p-1.5 hover:bg-zinc-100 flex items-center justify-center text-gray-500 rounded-full transition-colors"
        >
          <Plus className="h-[24px] w-[24px]" />
        </button>
      </div>

      {/* Company list */}
      <div className="overflow-y-auto custom-scroll flex-1 px-4 py-4 pt-0">
        <div className="flex flex-col gap-3">
          {/* Active Businesses */}
          <div className="flex flex-col gap-2.5">
            {activeAgencies.map((agency) => {
              const isActive = Number(agency.AgencyID) === Number(currentAgencyID);
              const agencyLogoUrl = getAgencyLogoUrl(agency);

              return (
                <div
                  key={agency.AgencyID}
                  tabIndex={0}
                  className={cn(
                    "flex items-center gap-4 px-4 py-3 pl-3 cursor-pointer transition-all duration-150 rounded-lg bg-white border border-zinc-200/60 shadow-xs focus:outline-none focus:ring-2 focus:ring-sahi-green",
                    isActive ? "border-sahi-green" : ""
                  )}
                  onClick={() => {
                    handleAgencySwitch(agency.AgencyID);
                    onClose();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleAgencySwitch(agency.AgencyID);
                      onClose();
                    }
                  }}
                >
                  {/* Logo / Icon */}
                  <div
                    className="w-11 h-11 rounded-md flex items-center justify-center shrink-0 overflow-hidden shadow-xs"
                    style={{ backgroundColor: agencyLogoUrl ? 'transparent' : isActive ? 'var(--sahi-green)' : '#f4f4f5' }}
                  >
                    {agencyLogoUrl ? (
                      <img
                        src={agencyLogoUrl}
                        alt={agency.CompanyName || agency.AgencyName}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <Building2
                      className="w-5 h-5"
                      style={{ display: agencyLogoUrl ? 'none' : 'block', color: isActive ? 'white' : '#a1a1aa' }}
                    />
                  </div>

                  {/* Text */}
                  <div className="min-w-0 flex-1">
                    <p className={`text-base font-semibold truncate ${isActive ? 'text-sahi-green' : 'text-zinc-800'}`}>
                      {agency.CompanyName || agency.AgencyName}
                    </p>
                    {agency.Designation && (
                      <p className="text-sm text-zinc-500 truncate mt-0.5">{agency.Designation}</p>
                    )}
                  </div>

                  {/* Active badge */}
                  {isActive && (
                    <div className="w-5 h-5 rounded-full bg-sahi-green flex items-center justify-center shrink-0 shadow">
                      <Check className="w-3 h-3 text-white stroke-[3px]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pending Invitations Subsection */}
          {pendingAgencies.length > 0 && (
            <div ref={pendingRef} className="flex flex-col gap-2 pt-2">
              <div className="flex items-center gap-2 px-1 pb-2">
                <span className="text-sm font-semibold text-zinc-500">
                  Pending Invitations
                </span>
                <div className="h-[1px] flex-1 bg-zinc-200"></div>
              </div>

              <div className="flex flex-col gap-2.5">
                {pendingAgencies.map((agency) => {
                  const agencyLogoUrl = getAgencyLogoUrl(agency);

                  return (
                    <div
                      key={agency.AgencyID}
                      className="flex flex-col gap-3 p-3.5 rounded-lg bg-white border border-zinc-200/60 shadow-xs"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-11 h-11 rounded-md flex items-center justify-center shrink-0 overflow-hidden shadow-xs"
                          style={{ backgroundColor: agencyLogoUrl ? 'transparent' : '#f4f4f5' }}
                        >
                          {agencyLogoUrl ? (
                            <img
                              src={agencyLogoUrl}
                              alt={agency.CompanyName || agency.AgencyName}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <Building2
                            className="w-5 h-5"
                            style={{ display: agencyLogoUrl ? 'none' : 'block', color: '#a1a1aa' }}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[15px] font-semibold text-zinc-800 truncate" title={agency.CompanyName || agency.AgencyName}>
                            {agency.CompanyName || agency.AgencyName}
                          </p>
                          {agency.Designation && (
                            <p className="text-xs text-zinc-500 truncate mt-0.5">{agency.Designation}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-start gap-2 pt-1">
                        <Button
                          size="sm"
                          className="h-8 px-3.5 bg-sahi-primary-color text-white border border-sahi-primary-color hover:bg-white hover:text-sahi-primary-color text-xs font-medium flex items-center gap-1.5 shadow-xs transition-colors"
                          onClick={(e) => handleAcceptInvitation(agency, e)}
                        >
                          <Check className="w-3.5 h-3.5 stroke-[2.5]" /> Accept
                        </Button>
                        <Button
                          size="sm"
                          className="h-8 px-3.5 bg-white text-sahi-primary-color border border-sahi-primary-color hover:bg-sahi-primary-color hover:text-white text-xs font-medium flex items-center gap-1.5 shadow-xs transition-colors"
                          onClick={(e) => handleRejectInvitation(agency, e)}
                        >
                          <X className="w-3.5 h-3.5" /> Decline
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Button Footer */}
      <div className="px-4 pb-4 shrink-0">
        <Button
          className="sahi-primary-button w-full flex items-center justify-center gap-2 h-11"
          onClick={onAddBusiness}
        >
          <Plus className="h-4 w-4" /> Add New Business
        </Button>
      </div>
    </>
  );
};

// Helper component for Desktop Dialog Body with dedicated scroll lifecycle
const DesktopDialogBody = ({
  userAgencies,
  currentAgencyID,
  handleAgencySwitch,
  handleAcceptInvitation,
  handleRejectInvitation,
  onClose,
  scrollToPending = false,
}) => {
  const activeAgencies = userAgencies.filter(a => Number(a.Status) !== 0);
  const pendingAgencies = userAgencies.filter(a => Number(a.Status) === 0);
  const pendingRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);

  React.useEffect(() => {
    if (scrollToPending && pendingRef.current) {
      const timer = setTimeout(() => {
        pendingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [scrollToPending]);

  return (
    <div ref={scrollContainerRef} className="overflow-y-auto px-4 max-h-[60vh] flex flex-col custom-scroll">
      <div className="flex flex-col gap-3 py-1">
        {/* Active Businesses */}
        <div className="flex flex-col gap-2.5">
          {activeAgencies.map((agency) => {
            const isActive = Number(agency.AgencyID) === Number(currentAgencyID);
            const agencyLogoUrl = getAgencyLogoUrl(agency);

            return (
              <div
                key={agency.AgencyID}
                tabIndex={0}
                className={cn(
                  "flex items-center gap-4 px-4 py-3.5 cursor-pointer transition-all duration-150 rounded-lg bg-white border shadow-xs",
                  isActive
                    ? "border-sahi-primary-color"
                    : "border-zinc-200/60 hover:border-zinc-300"
                )}
                onClick={() => {
                  handleAgencySwitch(agency.AgencyID);
                  onClose();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleAgencySwitch(agency.AgencyID);
                    onClose();
                  }
                }}
              >
                {/* Logo / Icon */}
                <div
                  className="w-12 h-12 rounded-md flex items-center justify-center shrink-0 overflow-hidden shadow-xs"
                  style={{ backgroundColor: agencyLogoUrl ? 'transparent' : isActive ? 'var(--sahi-primary-color)' : '#f4f4f5' }}
                >
                  {agencyLogoUrl ? (
                    <img
                      src={agencyLogoUrl}
                      alt={agency.CompanyName || agency.AgencyName}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <Building2
                    className="w-5 h-5"
                    style={{ display: agencyLogoUrl ? 'none' : 'block', color: isActive ? 'white' : '#a1a1aa' }}
                  />
                </div>

                {/* Text */}
                <div className="min-w-0 flex-1">
                  <p className={`text-[17px] font-semibold truncate ${isActive ? 'text-sahi-primary-color' : 'text-zinc-800'}`}>
                    {agency.CompanyName || agency.AgencyName}
                  </p>
                  {agency.Designation && (
                    <p className="text-sm text-zinc-500 truncate mt-0.5">{agency.Designation}</p>
                  )}
                </div>

                {/* Active badge */}
                {isActive && (
                  <div className="w-6 h-6 rounded-full bg-sahi-primary-color flex items-center justify-center shrink-0 shadow-xs">
                    <Check className="w-3.5 h-3.5 text-white stroke-[3px]" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pending Invitations Subsection */}
        {pendingAgencies.length > 0 && (
          <div ref={pendingRef} className="flex flex-col gap-2 pt-2">
            <div className="flex items-center gap-2 px-1 pb-2">
              <span className="text-sm font-semibold text-zinc-500">
                Pending Invitations
              </span>
              <div className="h-[1px] flex-1 bg-zinc-200"></div>
            </div>

            <div className="flex flex-col gap-2.5">
              {pendingAgencies.map((agency) => {
                const agencyLogoUrl = getAgencyLogoUrl(agency);

                return (
                  <div
                    key={agency.AgencyID}
                    className="flex items-center justify-between gap-3.5 px-4 py-3.5 rounded-lg bg-white border border-zinc-200/60 shadow-xs hover:border-zinc-300 transition-colors"
                  >
                    {/* Logo / Icon */}
                    <div
                      className="w-12 h-12 rounded-md flex items-center justify-center shrink-0 overflow-hidden shadow-xs"
                      style={{ backgroundColor: agencyLogoUrl ? 'transparent' : '#f4f4f5' }}
                    >
                      {agencyLogoUrl ? (
                        <img
                          src={agencyLogoUrl}
                          alt={agency.CompanyName || agency.AgencyName}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <Building2
                        className="w-5 h-5"
                        style={{ display: agencyLogoUrl ? 'none' : 'block', color: '#a1a1aa' }}
                      />
                    </div>

                    {/* Text */}
                    <div className="min-w-0 flex-1 pr-1">
                      <p className="text-[16px] font-semibold text-zinc-800 truncate" title={agency.CompanyName || agency.AgencyName}>
                        {agency.CompanyName || agency.AgencyName}
                      </p>
                      {agency.Designation && (
                        <p className="text-xs text-zinc-500 truncate mt-0.5">{agency.Designation}</p>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        className="h-8 px-3.5 bg-sahi-primary-color text-white border border-sahi-primary-color hover:bg-white hover:text-sahi-primary-color text-xs font-medium flex items-center gap-1.5 shadow-xs transition-colors"
                        onClick={(e) => handleAcceptInvitation(agency, e)}
                      >
                        <Check className="w-3.5 h-3.5 stroke-[2.5]" /> Accept
                      </Button>
                      <Button
                        size="sm"
                        className="h-8 px-3.5 bg-white text-sahi-primary-color border border-sahi-primary-color hover:bg-sahi-primary-color hover:text-white text-xs font-medium flex items-center gap-1.5 shadow-xs transition-colors"
                        onClick={(e) => handleRejectInvitation(agency, e)}
                      >
                        <X className="w-3.5 h-3.5" /> Decline
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AgencySelector = ({
  open,
  onOpenChange,
  userAgencies,
  currentAgencyID,
  handleAgencySwitch,
  scrollToPending = false,
}) => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [addBusinessOpen, setAddBusinessOpen] = React.useState(false);
  const [localAgencies, setLocalAgencies] = React.useState(userAgencies || []);
  const [confirmDialog, setConfirmDialog] = React.useState({ open: false, type: null, agency: null });

  React.useEffect(() => {
    setLocalAgencies(userAgencies || []);
  }, [userAgencies]);

  const handleAddBusinessClick = () => {
    const activeCount = (localAgencies || []).filter(a => Number(a.Status) !== 0).length;
    if (!isUserPremium() && activeCount >= 2) {
      toast.error("Upgrade to Premium to add more than two Agencies.");
      return;
    }
    setAddBusinessOpen(true);
  };

  const handleAcceptInvitation = (agency, e) => {
    e?.stopPropagation();
    // const activeCount = (localAgencies || []).filter(a => Number(a.Status) !== 0).length;
    // if (!isUserPremium() && activeCount >= 2) {
    //   toast.error("Upgrade to Premium to add more than two Agencies.");
    //   return;
    // }
    setConfirmDialog({ open: true, type: "ACCEPT", agency });
  };

  const handleRejectInvitation = (agency, e) => {
    e?.stopPropagation();
    setConfirmDialog({ open: true, type: "DECLINE", agency });
  };

  const executeAcceptInvitation = async (agency) => {
    const authData = getAuthData();
    if (!authData?.Mobile) {
      toast.error("User mobile number not found.");
      return;
    }

    dispatch(setGlobalLoading(true));
    try {
      await respondInvitation(authData.Mobile, authData.CC || "91", agency.AgencyID, "ACCEPT");

      // Clear sync DT and reload so fresh package data is loaded
      localStorage.setItem('MyLastSyncDT', '');
      window.location.reload();
    } catch (error) {
      toast.error(error.message || "Failed to accept invitation.");
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const executeRejectInvitation = async (agency) => {
    const authData = getAuthData();
    if (!authData?.Mobile) {
      toast.error("User mobile number not found.");
      return;
    }

    dispatch(setGlobalLoading(true));
    try {
      await respondInvitation(authData.Mobile, authData.CC || "91", agency.AgencyID, "REJECT");

      // Clear sync DT and reload so fresh package data is loaded
      localStorage.setItem('MyLastSyncDT', '');
      window.location.reload();
    } catch (error) {
      toast.error(error.message || "Failed to decline invitation.");
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleConfirmAction = async () => {
    const { type, agency } = confirmDialog;
    setConfirmDialog({ open: false, type: null, agency: null });
    if (!agency) return;

    if (type === "ACCEPT") {
      await executeAcceptInvitation(agency);
    } else if (type === "DECLINE") {
      await executeRejectInvitation(agency);
    }
  };

  // New agency was just created on the backend (its AgencyID/UserID/AgencyName come back in
  // RetData) - MyAgencies in localStorage has already been refreshed by the same response
  // (see addBusinessAPI / APIService.setPackageInfo), so switchAgency can find it immediately.
  const handleBusinessCreated = async (newBusiness) => {
    setAddBusinessOpen(false);
    const success = await switchAgency(newBusiness.AgencyID, dispatch);
    if (success) {
      onOpenChange(false);
      navigate("/");
    }
  };

  const renderSelector = () => {
    if (isMobile) {
      return (
        <Drawer open={open && !addBusinessOpen} onOpenChange={onOpenChange}>
          <DrawerContent className="p-0 [&>div:first-child]:hidden top-auto !h-auto rounded-t-[22px] max-h-[85dvh]">
            <div className="flex flex-col max-h-[85dvh]">
              <MobileDrawerBody
                onClose={() => onOpenChange(false)}
                userAgencies={localAgencies}
                currentAgencyID={currentAgencyID}
                handleAgencySwitch={handleAgencySwitch}
                onAddBusiness={handleAddBusinessClick}
                handleAcceptInvitation={handleAcceptInvitation}
                handleRejectInvitation={handleRejectInvitation}
                scrollToPending={scrollToPending}
              />
            </div>
          </DrawerContent>
        </Drawer>
      );
    }

    const hasPendingAgencies = localAgencies.some(a => Number(a.Status) === 0);

    return (
      <Dialog open={open && !addBusinessOpen} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            hasPendingAgencies ? "sm:max-w-[560px] md:max-w-[580px]" : "sm:max-w-md",
            "w-full animate-in fade-in zoom-in-95 duration-200"
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
            {/* Mobile header row - hidden on desktop, matches TaskCategoryManager */}
            <div className="sm:hidden flex items-center justify-between w-full h-16 px-1 -mx-1">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <button
                  type="button"
                  className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500"
                  onClick={() => onOpenChange(false)}
                >
                  <ArrowLeft className="h-[24px] w-[24px]" />
                </button>
                <DialogTitle className="text-lg font-semibold text-gray-900 truncate">
                  Select Business
                </DialogTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleAddBusinessClick}
                className="h-9 w-9 p-0 sm:text-text-color sm:hover:bg-gray-200 flex items-center justify-center"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>

            {/* Desktop title */}
            <DialogTitle className="hidden sm:block text-lg md:text-xl">
              Select Business
            </DialogTitle>

            {/* Desktop close button */}
            <div className="hidden sm:flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleAddBusinessClick}
                className="h-9 w-9 p-0 sm:text-text-color sm:hover:bg-gray-200 flex items-center justify-center"
              >
                <Plus className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon"
                className="h-9 w-9 p-0 sm:text-text-color sm:hover:bg-gray-200 flex items-center justify-center"
                tabIndex={-1}
                onClick={() => onOpenChange(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          {/* Company list body with auto-scroll */}
          <DesktopDialogBody
            userAgencies={localAgencies}
            currentAgencyID={currentAgencyID}
            handleAgencySwitch={handleAgencySwitch}
            handleAcceptInvitation={handleAcceptInvitation}
            handleRejectInvitation={handleRejectInvitation}
            onClose={() => onOpenChange(false)}
            scrollToPending={scrollToPending}
          />

          {/* Dialog Action Buttons */}
          <div className="flex sm:flex-row gap-3 px-4 pb-4">
            <Button onClick={handleAddBusinessClick} className="sahi-primary-button flex-1 sm:flex-none">
              <Plus className="w-3 h-3" /> Add New Business
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="sahi-secondary-button sm:flex-none sm:min-w-[100px]">
              <X className="w-3 h-3" /> Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      {renderSelector()}

      <AddBusinessDialog
        open={addBusinessOpen}
        onClose={() => setAddBusinessOpen(false)}
        onCreated={handleBusinessCreated}
      />

      {/* Confirmation AlertDialog for Accept / Decline Invitation */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmDialog({ open: false, type: null, agency: null });
          }
        }}
      >
        <AlertDialogContent className="w-[92%] sm:w-full max-w-md rounded-2xl p-5 sm:p-4 bg-white gap-4 border border-zinc-200/80 shadow-2xl">
          <AlertDialogHeader className="text-left gap-1.5">
            <AlertDialogTitle className="text-base sm:text-lg font-semibold text-zinc-900">
              {confirmDialog.type === "ACCEPT" ? "Accept Invitation" : "Decline Invitation"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-zinc-600 leading-normal">
              {confirmDialog.type === "ACCEPT" ? (
                <>
                  Are you sure you want to accept the invitation to join{" "}
                  <span className="font-semibold text-zinc-900">
                    "{confirmDialog.agency?.CompanyName || confirmDialog.agency?.AgencyName || "this business"}"
                  </span>
                  ?
                </>
              ) : (
                <>
                  Are you sure you want to decline the invitation to join{" "}
                  <span className="font-semibold text-zinc-900">
                    "{confirmDialog.agency?.CompanyName || confirmDialog.agency?.AgencyName || "this business"}"
                  </span>
                  ?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex !flex-row sm:!justify-start gap-2.5 pt-1">
            <Button
              onClick={handleConfirmAction}
              className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
            >
              {confirmDialog.type === "ACCEPT" ? (
                <>
                  <Check className="w-3.5 h-3.5 stroke-[2.5]" /> Accept
                </>
              ) : (
                <>
                  <Ban className="w-3.5 h-3.5" /> Decline
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, type: null, agency: null })}
              className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AgencySelector;
